import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import * as api from "../api/client";

/**
 * Pick further conversations whose charts/tables should be appended to an existing dashboard.
 *
 * Chats already on the dashboard are listed but not selectable: the server skips them anyway (adding
 * one twice would duplicate its cards), so offering them would promise something that quietly does
 * nothing. Everything a selected chat produced is added uncurated — the owner asked for these
 * specific chats — and unwanted cards can be removed individually afterwards.
 */
export default function AddChatsModal({ existingChatIds = [], onSubmit, onClose }) {
  const [chats, setChats] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const already = useMemo(() => new Set(existingChatIds), [existingChatIds]);

  useEffect(() => {
    let cancelled = false;
    api
      .listConversations()
      .then((r) => !cancelled && setChats(r || []))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, []);

  function toggle(cid) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (picked.size === 0) return;
    setSaving(true);
    setError("");
    try {
      await onSubmit([...picked]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const selectable = (chats || []).filter((c) => !already.has(c.id));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-modal" style={{ width: "min(560px, 100%)" }} onClick={(e) => e.stopPropagation()}>
        <div className="form-modal-head">
          <div className="form-modal-title">Add charts from another chat</div>
          <button className="icon-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>
        <Form onSubmit={submit}>
          <div className="form-modal-body">
            {error && <Alert variant="danger">{error}</Alert>}
            {chats === null ? (
              <Spinner animation="border" size="sm" />
            ) : selectable.length === 0 ? (
              <div className="col-desc-hint">
                Every one of your chats is already on this dashboard.
              </div>
            ) : (
              <div className="role-table-list">
                {chats.map((c) => {
                  const on = already.has(c.id);
                  return (
                    <div key={c.id} className="role-table-row">
                      <div className="role-table-row-head">
                        <Form.Check
                          type="checkbox"
                          id={`add-chat-${c.id}`}
                          label={c.title || "Untitled chat"}
                          checked={on || picked.has(c.id)}
                          disabled={on}
                          onChange={() => toggle(c.id)}
                        />
                        {on && <span className="pill neutral">already added</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="form-modal-foot">
            <Button variant="outline-secondary" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving || picked.size === 0}>
              {saving ? <Spinner animation="border" size="sm" /> : `Add ${picked.size || ""}`.trim()}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
